import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import { StatusTile } from '@/components/auth/StatusTile';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAuth = true }) => {
  const { isAuthenticated, isLoading, isAccessLoading, hasVoucherPortalAccess, logout } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto'></div>
          <p className='mt-2 text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated && isAccessLoading) {
    return (
      <StatusTile
        title='Zugangsdaten werden geladen'
        message='Ihre Verträge und Berechtigungen werden abgefragt. Bitte kurz warten.'
      />
    );
  }

  if (requireAuth && !isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to='/login' state={{ from: location }} replace />;
  }

  // Authenticated but no voucher portal access: do not show portal, show no-access and logout
  if (requireAuth && isAuthenticated && !hasVoucherPortalAccess) {
    return (
      <StatusTile
        variant='error'
        title='Kein Zugang zum Portal'
        message='Ihr Konto hat keine Berechtigung für das Gutscheinportal. Bitte wenden Sie sich an den Support oder melden Sie sich mit einem anderen Konto an.'
        actions={
          <Button onClick={() => logout()} variant='outline'>
            Abmelden
          </Button>
        }
      />
    );
  }

  if (!requireAuth && isAuthenticated) {
    // Redirect authenticated users away from auth pages (only if they have voucher access)
    const from = location.state?.from?.pathname || '/coupons';
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
};
